import { FC, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material'
import type { ModeloSimulacao, PoliticaProdutoRequest } from '@/types/simulacaoComissao'
import { MODELOS_SIMULACAO, ROTULO_MODELO } from '@/types/simulacaoComissao'
import type { Produto } from '@/types/produto'
import { errosDaFalha, HELPER_ORIGEM } from './errosFormulario'
import { inicioPadraoProposta } from './inicioPadrao'

interface Props {
  aberto: boolean
  produtos: Produto[]
  onFechar: () => void
  onSalvar: (request: PoliticaProdutoRequest) => Promise<void>
}

/**
 * Nova versão da política de um produto (modelo + natureza + vigência + origem). Produto vem
 * do catálogo (lista), nunca digitado; o modelo é um dos nomes de fórmula do motor. Criar
 * encerra a versão aberta anterior na data de início da nova (backend).
 */
export const NovaPoliticaProdutoDialog: FC<Props> = ({ aberto, produtos, onFechar, onSalvar }) => {
  const [produtoId, setProdutoId] = useState('')
  const [modelo, setModelo] = useState<ModeloSimulacao | ''>('')
  const [natureza, setNatureza] = useState('')
  const [vigenciaInicio, setVigenciaInicio] = useState(() => inicioPadraoProposta())
  const [origem, setOrigem] = useState('')
  const [erros, setErros] = useState<Record<string, string>>({})
  const [erroGeral, setErroGeral] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  useEffect(() => {
    if (aberto) {
      setProdutoId('')
      setModelo('')
      setNatureza('')
      setVigenciaInicio(inicioPadraoProposta())
      setOrigem('')
      setErros({})
      setErroGeral(null)
    }
  }, [aberto])

  const salvar = async () => {
    const faltando: Record<string, string> = {}
    if (!produtoId) faltando.produtoId = 'Escolha o produto'
    if (!modelo) faltando.modelo = 'Escolha o modelo de simulação'
    if (!origem.trim()) faltando.origem = 'Informe a origem (e-mail, ata ou contrato)'
    if (Object.keys(faltando).length) {
      setErros(faltando)
      return
    }
    const produto = produtos.find((p) => p.id === produtoId)
    setSalvando(true)
    setErros({})
    setErroGeral(null)
    try {
      await onSalvar({
        produtoId,
        produtoNome: produto?.nome ?? produtoId,
        ...(produto?.codigo ? { produtoCodigo: produto.codigo } : {}),
        modelo: modelo as ModeloSimulacao,
        natureza: natureza.trim() || null,
        vigenciaInicio: vigenciaInicio || null,
        origem: origem.trim(),
      })
      onFechar()
    } catch (e) {
      const r = errosDaFalha(e)
      setErros(r.porCampo)
      setErroGeral(r.geral)
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Dialog open={aberto} onClose={salvando ? undefined : onFechar} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Propor versão — modelo por produto</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {erroGeral && <Alert severity="error">{erroGeral}</Alert>}
          <TextField
            select
            SelectProps={{ native: true }}
            label="Produto"
            value={produtoId}
            onChange={(e) => setProdutoId(e.target.value)}
            error={!!erros.produtoId}
            helperText={erros.produtoId ?? 'Do catálogo de produtos'}
            InputLabelProps={{ shrink: true }}
            fullWidth
          >
            <option value="">Selecione…</option>
            {produtos.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nome}
              </option>
            ))}
          </TextField>
          <TextField
            select
            SelectProps={{ native: true }}
            label="Modelo de simulação"
            value={modelo}
            onChange={(e) => setModelo(e.target.value as ModeloSimulacao | '')}
            error={!!erros.modelo}
            helperText={erros.modelo ?? 'Fórmula do motor (PDF §4); "Sem fórmula" bloqueia a simulação'}
            InputLabelProps={{ shrink: true }}
            fullWidth
          >
            <option value="">Selecione…</option>
            {MODELOS_SIMULACAO.map((m) => (
              <option key={m} value={m}>
                {ROTULO_MODELO[m]}
              </option>
            ))}
          </TextField>
          <TextField
            label="Natureza (opcional)"
            value={natureza}
            onChange={(e) => setNatureza(e.target.value)}
            error={!!erros.natureza}
            helperText={erros.natureza ?? 'Em branco usa o texto padrão do modelo (recorrente/pontual)'}
            inputProps={{ maxLength: 120 }}
            fullWidth
          />
          <TextField
            label="Início da vigência"
            type="date"
            value={vigenciaInicio}
            onChange={(e) => setVigenciaInicio(e.target.value)}
            error={!!erros.vigenciaInicio}
            helperText={erros.vigenciaInicio ?? 'Padrão: amanhã — a aprovação precisa acontecer até o início'}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Origem"
            value={origem}
            onChange={(e) => setOrigem(e.target.value)}
            error={!!erros.origem}
            helperText={erros.origem ?? HELPER_ORIGEM}
            inputProps={{ maxLength: 200 }}
            required
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onFechar} disabled={salvando}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => void salvar()}
          disabled={salvando}
          startIcon={salvando ? <CircularProgress size={18} /> : undefined}
        >
          Enviar para aprovação
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default NovaPoliticaProdutoDialog
